/**
 * Image Helper Utilities - SOW Compliant
 * 
 * Implements the dual storage strategy:
 * - Synced Assets (product_image, variant_swatch, ad_creative, review_widget): 
 *   Use Shopify CDN URL first, fallback to DO Spaces
 * - Internal Assets (icons, dashboard banners, non-product images):
 *   Use DO Spaces URL directly, no Shopify sync
 * - Pre-Publication (pending approval):
 *   Use DO Spaces URL until Shopify CDN URL is available
 */

// DO Spaces endpoint - should match backend configuration
const DO_SPACES_ENDPOINT = 'https://mcs-master-assets.sgp1.digitaloceanspaces.com';

// Asset types that sync to Shopify CDN
const SYNCED_ASSET_TYPES = ['product_image', 'variant_swatch', 'ad_creative', 'review_widget'];

// Asset types that use DO Spaces only (no Shopify sync)
const INTERNAL_ASSET_TYPES = ['internal', 'icon', 'dashboard', 'ui'];

/**
 * Get the appropriate image URL based on asset type and availability.
 * 
 * SOW Rules:
 * - Synced assets: Shopify CDN > DO Spaces > Local fallback
 * - Internal assets: DO Spaces only
 * - Pre-publication: DO Spaces (until Shopify CDN available)
 * 
 * @param {string|object} imageSource - URL string or media object
 * @param {string} assetType - Asset type (product_image, internal, etc.)
 * @returns {string|null} - Resolved image URL
 */
export const getImageUrl = (imageSource, assetType = 'product_image') => {
  if (!imageSource) return null;
  
  // Handle media objects with multiple URL fields
  if (typeof imageSource === 'object') {
    return getImageUrlFromObject(imageSource, assetType);
  }
  
  // Handle string URLs
  let imageUrl = imageSource;

  // iter-242 — Defensive URL self-heal. Some legacy migration data
  // stored protocols without the colon (e.g. `https//foo.com/img.jpg`),
  // producing dozens of ERR_NAME_NOT_RESOLVED errors per dashboard
  // render. Repair the protocol inline so the URL becomes loadable
  // without a DB migration. Matches both `https//` and `http//` at
  // the start of the string.
  if (typeof imageUrl === 'string') {
    imageUrl = imageUrl.replace(/^(https?)\/\//i, '$1://');
  }
  
  // Already a full Shopify CDN URL - use as is for synced assets
  if (imageUrl.includes('cdn.shopify.com')) {
    return imageUrl;
  }
  
  // Already a full DO Spaces URL - use as is
  if (imageUrl.includes('digitaloceanspaces.com')) {
    return imageUrl;
  }
  
  // Full external URL - use as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // Convert local paths to DO Spaces URLs
  return convertLocalPathToDoSpaces(imageUrl);
};

/**
 * iter-242 — Repair legacy `https//` / `http//` URLs (missing colon)
 * that some import/migration pipelines wrote into vendor catalogues.
 * Safe to call on any string; non-strings/empties pass through.
 */
const _healProtocol = (u) => {
  if (typeof u !== 'string' || !u) return u;
  return u.replace(/^(https?)\/\//i, '$1://');
};

/**
 * Get URL from a media object based on asset type
 */
function getImageUrlFromObject(mediaObj, assetType) {
  const isSyncedAsset = SYNCED_ASSET_TYPES.includes(assetType);
  
  if (isSyncedAsset) {
    // For synced assets: prefer Shopify CDN > DO Spaces > file_url
    if (mediaObj.shopify_cdn_url) {
      return _healProtocol(mediaObj.shopify_cdn_url);
    }
    // cdn_url from imports (Shopify CDN URLs)
    if (mediaObj.cdn_url && (mediaObj.cdn_url.includes('cdn.shopify.com') || mediaObj.cdn_url.startsWith('http'))) {
      return _healProtocol(mediaObj.cdn_url);
    }
    if (mediaObj.do_source_url) {
      return _healProtocol(mediaObj.do_source_url);
    }
    if (mediaObj.primary_url) {
      return _healProtocol(mediaObj.primary_url);
    }
  } else {
    if (mediaObj.cdn_url && mediaObj.cdn_url.startsWith('http')) {
      return _healProtocol(mediaObj.cdn_url);
    }
    if (mediaObj.do_source_url) {
      return _healProtocol(mediaObj.do_source_url);
    }
    if (mediaObj.primary_url) {
      return _healProtocol(mediaObj.primary_url);
    }
  }
  
  // Fallback to file_url and convert if needed
  const fileUrl = mediaObj.file_url || mediaObj.url || mediaObj.src;
  if (fileUrl) {
    return getImageUrl(_healProtocol(fileUrl), assetType);
  }
  
  return null;
}

/**
 * Convert local file paths to DO Spaces URLs
 * Priority: backend file server first (works for local files), DO Spaces as fallback
 */
function convertLocalPathToDoSpaces(imageUrl) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // /api/uploads/media/admin/xxx.jpg
  if (imageUrl.startsWith('/api/uploads/media/')) {
    // Try backend first — it serves this path directly
    return `${BACKEND_URL}${imageUrl}`;
  }

  // /api/files/image/xxx.jpg
  if (imageUrl.startsWith('/api/files/image/')) {
    return `${BACKEND_URL}${imageUrl}`;
  }

  // /api/files/category/xxx.jpg
  if (imageUrl.startsWith('/api/files/')) {
    return `${BACKEND_URL}${imageUrl}`;
  }

  // Any other /api/ path — prepend backend URL
  if (imageUrl.startsWith('/api/')) {
    return `${BACKEND_URL}${imageUrl}`;
  }

  // Just a filename without path — attempt DO Spaces admin folder as last resort
  if (!imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
    return `${DO_SPACES_ENDPOINT}/admin/${imageUrl}`;
  }

  // Default: prepend backend URL
  return `${BACKEND_URL}${imageUrl}`;
}

/**
 * Get the first image URL from a media array
 * Supports both string arrays and object arrays
 * 
 * @param {Array} media - Array of URLs or media objects
 * @param {string} assetType - Asset type for URL resolution
 * @returns {string|null} - First resolved image URL
 */
export const getFirstImage = (media, assetType = 'product_image') => {
  if (!media || !Array.isArray(media) || media.length === 0) {
    return null;
  }
  
  const firstItem = media[0];
  
  // If media is an array of strings (URLs)
  if (typeof firstItem === 'string') {
    return getImageUrl(firstItem, assetType);
  }
  
  // If media is an array of objects with url/file_url/do_source_url properties
  if (typeof firstItem === 'object') {
    return getImageUrlFromObject(firstItem, assetType);
  }
  
  return null;
};

/**
 * Get all possible fallback URLs for an image
 * Returns array of URLs to try in order based on asset type
 * 
 * @param {string|object} imageSource - URL string or media object
 * @param {string} assetType - Asset type
 * @returns {Array<string>} - Array of URLs to try
 */
export const getImageFallbackUrls = (imageSource, assetType = 'product_image') => {
  if (!imageSource) return [];
  
  const urls = [];
  const isSyncedAsset = SYNCED_ASSET_TYPES.includes(assetType);
  
  // Handle media objects
  if (typeof imageSource === 'object') {
    if (isSyncedAsset) {
      // Synced: Shopify CDN > DO Spaces > file_url
      if (imageSource.shopify_cdn_url) urls.push(_healProtocol(imageSource.shopify_cdn_url));
      if (imageSource.do_source_url && !urls.includes(_healProtocol(imageSource.do_source_url))) {
        urls.push(_healProtocol(imageSource.do_source_url));
      }
    } else {
      // Internal: DO Spaces only
      if (imageSource.do_source_url) urls.push(_healProtocol(imageSource.do_source_url));
    }
    
    // Add primary_url if different
    if (imageSource.primary_url && !urls.includes(_healProtocol(imageSource.primary_url))) {
      urls.push(_healProtocol(imageSource.primary_url));
    }
    
    // Add file_url as last resort
    const fileUrl = imageSource.file_url || imageSource.url;
    if (fileUrl) {
      const resolvedUrl = convertLocalPathToDoSpaces(_healProtocol(fileUrl));
      if (!urls.includes(resolvedUrl)) urls.push(resolvedUrl);
    }
    
    return urls;
  }
  
  // Handle string URLs
  const primaryUrl = getImageUrl(imageSource, assetType);
  if (primaryUrl) urls.push(primaryUrl);

  // For backend-served paths, also add DO Spaces as a secondary fallback
  if (typeof imageSource === 'string') {
    let filename = null;
    let doSpacesFolder = 'admin';

    if (imageSource.startsWith('/api/uploads/media/')) {
      const relativePath = imageSource.replace('/api/uploads/media/', '');
      const doUrl = `${DO_SPACES_ENDPOINT}/${relativePath}`;
      if (!urls.includes(doUrl)) urls.push(doUrl);
    } else if (imageSource.startsWith('/api/files/image/')) {
      filename = imageSource.replace('/api/files/image/', '');
    } else if (imageSource.startsWith('/api/files/')) {
      const parts = imageSource.replace('/api/files/', '').split('/');
      filename = parts[parts.length - 1];
      doSpacesFolder = parts.length >= 2 && parts[0] !== filename ? parts[0] : 'admin';
    } else if (imageSource.includes('/')) {
      const parts = imageSource.split('/');
      filename = parts[parts.length - 1];
    } else {
      filename = imageSource;
    }

    if (filename) {
      const doUrl = `${DO_SPACES_ENDPOINT}/${doSpacesFolder}/${filename}`;
      if (!urls.includes(doUrl)) urls.push(doUrl);
    }
  }

  return urls;
};

/**
 * Determine if an asset type should sync to Shopify
 * @param {string} assetType - Asset type
 * @returns {boolean}
 */
export const shouldSyncToShopify = (assetType) => {
  return SYNCED_ASSET_TYPES.includes(assetType);
};

/**
 * Determine if an asset type is internal (no Shopify sync)
 * @param {string} assetType - Asset type
 * @returns {boolean}
 */
export const isInternalAsset = (assetType) => {
  return INTERNAL_ASSET_TYPES.includes(assetType);
};

/**
 * Default placeholder image URL
 */
export const PLACEHOLDER_IMAGE = '/placeholder.svg';

/**
 * Get placeholder image based on type
 */
export const getPlaceholderImage = (type = 'default') => {
  return PLACEHOLDER_IMAGE;
};

// Export constants for external use
export { DO_SPACES_ENDPOINT, SYNCED_ASSET_TYPES, INTERNAL_ASSET_TYPES };

export default {
  getImageUrl,
  getFirstImage,
  getImageFallbackUrls,
  shouldSyncToShopify,
  isInternalAsset,
  PLACEHOLDER_IMAGE,
  getPlaceholderImage,
  DO_SPACES_ENDPOINT,
  SYNCED_ASSET_TYPES,
  INTERNAL_ASSET_TYPES
};
